import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import qs from 'shared/utils/qs'

import { API_URL } from '@/config'
import { getUser } from '@/features/auth'

const encodePath = (value) => encodeURIComponent(value)

const baseQuery = fetchBaseQuery({
    baseUrl: API_URL,
    prepareHeaders: (headers) => {
        const user = getUser()
        const token = user?.id_token
        if (token) {
            headers.set('authorization', `Bearer ${token}`)
        }
        return headers
    },
    paramsSerializer: (params) => {
        return qs.stringify(params)
    },
})

export const resumeApi = createApi({
    reducerPath: 'resumeApi',
    baseQuery,
    tagTypes: ['ResumeCandidate', 'ResumeEntry'],
    endpoints: (builder) => ({
        getResumeCandidate: builder.query({
            query: (candidateId) => ({
                url: `/v1/resumes/users/${encodePath(candidateId)}`,
            }),
            providesTags: (result, error, candidateId) => [
                { type: 'ResumeCandidate', id: candidateId },
                { type: 'ResumeEntry', id: `LIST_${candidateId}` },
            ],
        }),
        createResumeCandidate: builder.mutation({
            query: (body) => ({
                url: '/v1/resumes/users',
                method: 'POST',
                body,
            }),
            invalidatesTags: (result, error, { salesforce_id }) =>
                salesforce_id
                    ? [{ type: 'ResumeCandidate', id: salesforce_id }]
                    : ['ResumeCandidate'],
        }),
        updateResumeCandidate: builder.mutation({
            query: ({ candidateId, ...body }) => ({
                url: `/v1/resumes/users/${encodePath(candidateId)}`,
                method: 'PUT',
                body,
            }),
            invalidatesTags: (result, error, { candidateId }) => [
                { type: 'ResumeCandidate', id: candidateId },
            ],
        }),
        releaseResumeLoginLock: builder.mutation({
            query: (candidateId) => ({
                url: `/v1/resumes/users/${encodePath(
                    candidateId
                )}/lock-release`,
                method: 'POST',
            }),
            invalidatesTags: (result, error, candidateId) => [
                { type: 'ResumeCandidate', id: candidateId },
            ],
        }),
        createResumeEntry: builder.mutation({
            query: ({ candidateId, ...body }) => ({
                url: `/v1/resumes/users/${encodePath(candidateId)}/entries`,
                method: 'POST',
                body,
            }),
            invalidatesTags: (result, error, { candidateId, external_id }) => [
                { type: 'ResumeCandidate', id: candidateId },
                { type: 'ResumeEntry', id: `LIST_${candidateId}` },
                { type: 'ResumeEntry', id: external_id },
            ],
        }),
        getResumePdf: builder.query({
            query: ({ candidateId, externalId }) => ({
                url: `/v1/resumes/users/${encodePath(
                    candidateId
                )}/entries/${encodePath(externalId)}/resume.pdf`,
                responseHandler: (response) => response.blob(),
            }),
            providesTags: (result, error, { externalId }) => [
                { type: 'ResumeEntry', id: externalId },
            ],
        }),
        getCareerPdf: builder.query({
            query: ({ candidateId, externalId }) => ({
                url: `/v1/resumes/users/${encodePath(
                    candidateId
                )}/entries/${encodePath(externalId)}/career.pdf`,
                responseHandler: (response) => response.blob(),
            }),
            providesTags: (result, error, { externalId }) => [
                { type: 'ResumeEntry', id: externalId },
            ],
        }),
    }),
})

export const {
    useGetResumeCandidateQuery,
    useLazyGetResumeCandidateQuery,
    useCreateResumeCandidateMutation,
    useUpdateResumeCandidateMutation,
    useReleaseResumeLoginLockMutation,
    useCreateResumeEntryMutation,
    useGetResumePdfQuery,
    useLazyGetResumePdfQuery,
    useGetCareerPdfQuery,
    useLazyGetCareerPdfQuery,
} = resumeApi
